import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FormEvent, useCallback, useMemo, useState } from "react";
import { usePrescribe } from "src/context/PrescribeProvider/usePrescribe";
import { isTruthy } from "src/utils/isTruthy";
import StepsFooter from "../StepsFooter";
import { usePrescription } from "src/context/PrescriptionProvider";
import { useUpdatePrescriptionMutation } from "src/api/services/prescriptionsApi";
import { useCreateOrderMutation } from "src/api/services/ordersApi";
import { useDosageInstructions } from "src/hooks/useDosageInstructions";
import MedicalSupplies from "src/shared/MedicalSupplies";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { useTitration } from "src/context/TitrationProvider/useTitration";
import { Treatments } from "src/api/types";

interface Props1 {
  onSuccess: () => Promise<void>;
}

const ReviewStep = ({ onSuccess }: Props1) => {
  const [loading, setLoading] = useState(false);
  const [updatePrescription] = useUpdatePrescriptionMutation();
  const [createOrder] = useCreateOrderMutation();
  const {
    medications,
    selectedMedications,
    supplyId,
    durationInDays,
    medication,
    dosages,
    quantity,
    totalPills,
  } = usePrescribe();
  const { instructions, template } = useDosageInstructions(
    medication,
    medication?.pharmacy.name ?? "",
    dosages,
  );
  const patientId = usePrescription((p) => p.patient_id);
  const prescriptionId = usePrescription((p) => p.id);
  const productId = usePrescription((p) => p.product_id);
  const productCategoryId = usePrescription((p) => p.product_category_id);
  const billingModel = usePrescription((p) => p.product?.billing_model);
  const medicationIds = usePrescription((p) => p.medications);
  const prescriptionType = usePrescription((p) => p.type);
  const [error, setError] = useState("");
  const invoiceId = usePrescription(
    (p) => (p.metadata as Record<string, string>)?.invoice_id,
  );
  const refills = usePrescription((p) => p.refills);
  const titration = useTitration();

  const logError = useSentryLogger("error");

  const selected = useMemo(() => {
    return selectedMedications
      .map((id) => medications.find((m) => m.id === id))
      .filter(isTruthy);
  }, [medications, selectedMedications]);

  const isGlp1 = useMemo(
    () =>
      selected.some(
        (medication) =>
          medication.form === "injectable" &&
          medication.treatment.name === Treatments.WeightLoss,
      ),
    [selected],
  );
  const formatInstructions = useMemo(() => {
    return instructions.map((instruction) =>
      template
        .replace("{{week}}", instruction.week ?? "")
        .replace("{{amount}}", instruction.amount)
        .replace("{{dosage}}", instruction.dosage)
        .replace(
          "{{duration}}",
          `${instruction.interval_count} ${instruction.interval}`,
        ),
    );
  }, [instructions, template]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (durationInDays == null || durationInDays === 0) {
        setError("Duration cannot be null or 0");
        return;
      }

      setLoading(true);

      try {
        await updatePrescription({
          medication_ids:
            (medicationIds?.length || 0) > 0
              ? undefined
              : selectedMedications.filter(isTruthy),
          id: prescriptionId,
          dispense_quantity:
            totalPills != null
              ? totalPills.toString()
              : quantity
                ? quantity.toString()
                : selectedMedications.length.toString(),
          instructions: instructions.map((instruction, index) => ({
            amount: instruction.amount,
            duration: `${instruction.interval_count} ${instruction.interval}s`,
            dosage: instruction.dosage,
            interval: instruction.interval,
            interval_count: instruction.interval_count,
            value: formatInstructions[index],
          })),
          supply_id: supplyId,
          status: "prescribed",
          days_supply: durationInDays ?? 28,
          prescribed_at: new Date().toISOString(),
          refills: refills ?? 0,
          pharmacy_id: selected[0].pharmacy_id,
        }).unwrap();

        if (selected[0].pharmacy_id != null) {
          await createOrder({
            prescription_id: prescriptionId,
            status: billingModel === "one_time" ? "paid" : "created",
            patient_id: patientId,
            pharmacy_id: selected[0].pharmacy_id,
            product_id: productId,
            product_category_id: productCategoryId,
            invoice_id: billingModel === "one_time" ? invoiceId : null,
            type: prescriptionType,
          }).unwrap();
        }
        onSuccess();
      } catch (err) {
        logError(`Error submitting prescription: ${prescriptionId}`, err);
      } finally {
        setLoading(false);
      }
    },
    [
      durationInDays,
      updatePrescription,
      medicationIds?.length,
      selectedMedications,
      prescriptionId,
      totalPills,
      quantity,
      instructions,
      supplyId,
      refills,
      selected,
      onSuccess,
      formatInstructions,
      createOrder,
      billingModel,
      patientId,
      productId,
      productCategoryId,
      invoiceId,
      prescriptionType,
      logError,
    ],
  );

  return (
    <Stack component="form" onSubmit={handleSubmit} gap={2}>
      <Stack gap={1}>
        <Typography fontWeight={600}>Medications:</Typography>
        {selected.map((medication, index) => (
          <FormControl sx={{ gap: 1 }} key={`${medication.id} - ${index}`}>
            <TextField
              disabled
              defaultValue={`${medication.name} ${
                medication.dosage !== "N/A" ? `- ${medication.dosage}` : ""
              }`}
              helperText={`Formula: ${medication.strength}`}
            />
          </FormControl>
        ))}
      </Stack>

      {isGlp1 ? <MedicalSupplies pharmacyId={selected[0].pharmacy_id} /> : null}

      <Stack gap={1}>
        <Typography fontWeight={600}>Instructions:</Typography>
        {formatInstructions.map((instruction, index) => (
          <FormControl sx={{ gap: 1 }} key={index}>
            {titration != null ? (
              <FormLabel>{`${instructions[index].week} [${instructions[index].dosage}]`}</FormLabel>
            ) : null}
            <TextField multiline minRows={2} defaultValue={instruction} />
          </FormControl>
        ))}
      </Stack>

      {error ? (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      ) : null}

      <StepsFooter isLast loading={loading} />
    </Stack>
  );
};

export default ReviewStep;
